import { NODE_KEYS, type DocumentFont, type NodeType } from '@flobi/artwork-core';

/**
 * Copy properties / Paste properties (⌥⌘C, ⌥⌘V), as Figma has them: one layer's look —
 * fill, border, effects, opacity, blend, corners and, from a text, its type — carried onto
 * others, leaving their size, place and contents alone.
 *
 * Kept apart from the canvas so the rules about what goes where can be read and tested on
 * their own: a shape's fill is a text's colour and the other way round, corners travel
 * between rectangles and stars, and whatever a layer cannot hold is simply not written.
 */
export interface CopiedProperties {
  readonly from: NodeType;
  readonly opacity?: number;
  readonly blendMode?: string;
  /** What it is painted with: a text's own colour (its style's fills) counts as its fill. */
  readonly fills?: unknown[];
  readonly strokes?: unknown[];
  readonly strokeStyle?: Record<string, unknown>;
  readonly effects?: unknown[];
  /** Corners, as four radii, whichever way the layer keeps them. */
  readonly radii?: [number, number, number, number];
  /** A text's whole style: family, weight, size, spacing, case and the rest. */
  readonly textStyle?: Record<string, unknown>;
}

type Layer = { readonly type: string } & Record<string, unknown>;

/** Containers draw their children as they are unless told otherwise: their neutral blend. */
const PASS_THROUGH = new Set(['artboard', 'group', 'blend', 'cloner']);
/** Things with no look to copy or to take one. */
const NO_LOOK = new Set(['slice', 'effector']);
/** A picture's fills would cover the picture; a line is drawn by its border alone. */
const NO_FILL = new Set(['image', 'line']);

const clone = <T>(value: T): T => structuredClone(value);

/** The look of one layer, or null when it has none worth carrying. */
export function propertiesOf(node: Layer): CopiedProperties | null {
  if (NO_LOOK.has(node.type)) return null;
  const type = node.type as NodeType;
  const out: { -readonly [K in keyof CopiedProperties]: CopiedProperties[K] } = { from: type };
  if (typeof node.opacity === 'number') out.opacity = node.opacity;
  if (typeof node.blendMode === 'string') out.blendMode = node.blendMode;
  const style = node.style as Record<string, unknown> | undefined;
  if (type === 'text' && style) {
    out.textStyle = clone(style);
    if (Array.isArray(style.fills)) out.fills = clone(style.fills);
  } else if (!NO_FILL.has(type) && Array.isArray(node.fills)) out.fills = clone(node.fills);
  if (Array.isArray(node.strokes)) out.strokes = clone(node.strokes);
  if (node.strokeStyle && typeof node.strokeStyle === 'object') out.strokeStyle = clone(node.strokeStyle as Record<string, unknown>);
  if (Array.isArray(node.effects)) out.effects = clone(node.effects);
  if (Array.isArray(node.radii) && node.radii.length === 4) out.radii = clone(node.radii as [number, number, number, number]);
  else if (typeof node.cornerRadius === 'number') {
    const r = node.cornerRadius;
    out.radii = [r, r, r, r];
  }
  return out;
}

/**
 * What to write on `target` to give it the copied look: only fields it can hold, so a group
 * takes the opacity and effects and leaves the fill, and a text takes a shape's fill as its
 * colour. An empty object when nothing applies.
 */
export function propertiesFor(target: Layer, copied: CopiedProperties): Record<string, unknown> {
  if (NO_LOOK.has(target.type)) return {};
  const keys = NODE_KEYS[target.type as NodeType];
  if (!keys) return {};
  const props: Record<string, unknown> = {};
  if (copied.opacity !== undefined && keys.has('opacity')) props.opacity = copied.opacity;
  if (copied.blendMode !== undefined && keys.has('blendMode')) {
    // Normal on a shape and pass-through on a group both mean "nothing special": each
    // takes its own kind of nothing, so a group pasted with a rectangle's look still lets
    // its children blend with what is under it.
    const neutral = copied.blendMode === 'normal' || copied.blendMode === 'pass-through';
    props.blendMode = neutral ? (PASS_THROUGH.has(target.type) ? 'pass-through' : 'normal') : copied.blendMode;
  }
  if (target.type === 'text') {
    const style = { ...((target.style as Record<string, unknown> | undefined) ?? {}), ...(copied.textStyle ? clone(copied.textStyle) : {}) };
    // A shape with no fill would leave the words invisible: they keep their colour then.
    if (copied.fills && copied.fills.length > 0) style.fills = clone(copied.fills);
    if (copied.textStyle || (copied.fills && copied.fills.length > 0)) props.style = style;
  } else if (copied.fills !== undefined && keys.has('fills') && !NO_FILL.has(target.type)) props.fills = clone(copied.fills);
  if (copied.strokes !== undefined && keys.has('strokes')) props.strokes = clone(copied.strokes);
  if (copied.strokeStyle !== undefined && keys.has('strokeStyle')) props.strokeStyle = clone(copied.strokeStyle);
  if (copied.effects !== undefined && keys.has('effects')) props.effects = clone(copied.effects);
  if (copied.radii) {
    if (keys.has('radii')) props.radii = clone(copied.radii);
    else if (keys.has('cornerRadius')) props.cornerRadius = copied.radii[0];
  }
  return props;
}

/**
 * The look last copied, with the font it names when it came off a text. Held for the tab
 * rather than for one file, as Figma holds it, so a look copied in one file pastes in the
 * next — and the font goes with it, which that file may not have.
 */
export interface HeldLook {
  readonly properties: CopiedProperties;
  readonly font?: DocumentFont;
}
let held: HeldLook | null = null;
export const holdLook = (look: HeldLook | null) => {
  held = look;
};
export const heldLook = (): HeldLook | null => held;
